/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import {
  startTransition,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { graphql, useLazyLoadQuery, useQueryLoader } from "react-relay";
import { Outlet, useNavigate, useParams } from "react-router";

import {
  Flex,
  LazyTabPanel,
  Loading,
  Tab,
  TabList,
  Tabs,
} from "@phoenix/components";
import {
  ConnectedTimeRangeSelector,
  useTimeRange,
} from "@phoenix/components/datetime";
import { useProjectContext } from "@phoenix/contexts/ProjectContext";
import { StreamStateProvider } from "@phoenix/contexts/StreamStateContext";
import { useProjectRootPath } from "@phoenix/hooks/useProjectRootPath";

import type { ProjectPageQueriesProjectConfigQuery as ProjectPageProjectConfigQueryType } from "./__generated__/ProjectPageQueriesProjectConfigQuery.graphql";
import type { ProjectPageQueriesSessionsQuery as ProjectPageSessionsQueryType } from "./__generated__/ProjectPageQueriesSessionsQuery.graphql";
import type { ProjectPageQueriesSpansQuery as ProjectPageSpansQueryType } from "./__generated__/ProjectPageQueriesSpansQuery.graphql";
import type { ProjectPageQueriesTracesQuery as ProjectPageTracesQueryType } from "./__generated__/ProjectPageQueriesTracesQuery.graphql";
import type { ProjectPageQuery as ProjectPageQueryType } from "./__generated__/ProjectPageQuery.graphql";
import { ProjectPageHeader } from "./ProjectPageHeader";
import {
  ProjectPageQueriesProjectConfigQuery,
  ProjectPageQueriesSessionsQuery,
  ProjectPageQueriesSpansQuery,
  ProjectPageQueriesTracesQuery,
  ProjectPageQueryReferenceContext,
} from "./ProjectPageQueries";
import { StreamToggle } from "./StreamToggle";

const mainCSS = css`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  .tabs {
    flex: 1 1 auto;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    div[role="tablist"] {
      flex: none;
      .react-aria-Tab,
      div[role="tab"] {
        font-size: 16px !important;
        font-weight: 800 !important;
        color: #1e293b !important;
        text-shadow: 0 0 0.25px currentColor;
        letter-spacing: 0.2px;
        opacity: 1 !important;

        &[data-selected] {
          color: #00685f !important;
          --tab-border-color: #00685f;
          border-bottom: 2.5px solid #00685f !important;
          text-shadow: 0 0 0.25px #00685f;
        }

        &:hover:not([data-selected]) {
          color: #0f172a !important;
        }
      }
    }
    .tabs__pane-container {
      flex: 1 1 auto;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      div[role="tabpanel"]:not([hidden]) {
        flex: 1 1 auto;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }
    }
  }
`;

export function ProjectPage() {
  const { projectId } = useParams();
  const { timeRange } = useTimeRange();
  return (
    <Suspense fallback={<Loading />}>
      <ProjectPageContent
        projectId={projectId as string}
        timeRange={timeRange}
      />
    </Suspense>
  );
}

const TABS = ["spans", "traces", "sessions", "dashboard", "metrics", "config"] as const;

/**
 * Type guard for the tab path in the URL
 */
const isTab = (tab: string): tab is (typeof TABS)[number] => {
  return TABS.includes(tab as (typeof TABS)[number]);
};

const TAB_INDEX_MAP: Record<(typeof TABS)[number], number> = {
  spans: 0,
  traces: 1,
  sessions: 2,
  dashboard: 3,
  metrics: 4,
  config: 5,
};

export function ProjectPageContent({
  projectId,
  timeRange,
}: {
  projectId: string;
  timeRange: OpenTimeRange;
}) {
  return (
    <StreamStateProvider>
      <ProjectPageContentBody projectId={projectId} timeRange={timeRange} />
    </StreamStateProvider>
  );
}

function ProjectPageContentBody({
  projectId,
  timeRange,
}: {
  projectId: string;
  timeRange: OpenTimeRange;
}) {
  const treatOrphansAsRoots = useProjectContext(
    (state) => state.treatOrphansAsRoots
  );
  const timeRangeVariable = useMemo(() => {
    return {
      start: timeRange?.start?.toISOString(),
      end: timeRange?.end?.toISOString(),
    };
  }, [timeRange]);
  const navigate = useNavigate();
  const { rootPath, tab } = useProjectRootPath();
  const data = useLazyLoadQuery<ProjectPageQueryType>(
    graphql`
      query ProjectPageQuery($id: ID!, $timeRange: TimeRange!) {
        project: node(id: $id) {
          ... on Project {
            ...ProjectPageHeader_stats
            ...StreamToggle_data
          }
        }
      }
    `,
    {
      id: projectId as string,
      timeRange: timeRangeVariable,
    },
    {
      fetchPolicy: "store-and-network",
      fetchKey: `${projectId}-${timeRangeVariable.start}-${timeRangeVariable.end}`,
    }
  );
  const [tracesQueryReference, loadTracesQuery, disposeTracesQuery] =
    useQueryLoader<ProjectPageTracesQueryType>(ProjectPageQueriesTracesQuery);
  const [spansQueryReference, loadSpansQuery, disposeSpansQuery] =
    useQueryLoader<ProjectPageSpansQueryType>(ProjectPageQueriesSpansQuery);
  const [sessionsQueryReference, loadSessionsQuery, disposeSessionsQuery] =
    useQueryLoader<ProjectPageSessionsQueryType>(
      ProjectPageQueriesSessionsQuery
    );
  const [
    projectConfigQueryReference,
    loadProjectConfigQuery,
    disposeProjectConfigQuery,
  ] = useQueryLoader<ProjectPageProjectConfigQueryType>(
    ProjectPageQueriesProjectConfigQuery
  );
  const tabIndex = isTab(tab) ? TAB_INDEX_MAP[tab] : 0;
  useEffect(() => {
    if (tabIndex === TAB_INDEX_MAP.spans) {
      loadSpansQuery({
        id: projectId as string,
        timeRange: timeRangeVariable,
        orphanSpanAsRootSpan: treatOrphansAsRoots,
      });
    } else if (tabIndex === TAB_INDEX_MAP.traces) {
      loadTracesQuery({
        id: projectId as string,
        timeRange: timeRangeVariable,
      });
    } else if (tabIndex === TAB_INDEX_MAP.sessions) {
      loadSessionsQuery({
        id: projectId as string,
        timeRange: timeRangeVariable,
      });
    } else if (tabIndex === TAB_INDEX_MAP.config) {
      loadProjectConfigQuery({
        id: projectId as string,
      });
    }

    return () => {
      disposeSpansQuery();
      disposeSessionsQuery();
      disposeTracesQuery();
      disposeProjectConfigQuery();
    };
  }, [
    loadTracesQuery,
    projectId,
    timeRangeVariable,
    tabIndex,
    disposeSpansQuery,
    disposeSessionsQuery,
    disposeTracesQuery,
    loadSpansQuery,
    loadSessionsQuery,
    loadProjectConfigQuery,
    disposeProjectConfigQuery,
    treatOrphansAsRoots,
  ]);

  const onTabChange = useCallback(
    (index: number) => {
      startTransition(() => {
        if (index === 1) {
          navigate(`${rootPath}/traces`);
        } else if (index === 2) {
          navigate(`${rootPath}/sessions`);
        } else if (index === 3) {
          navigate(`${rootPath}/dashboard`);
        } else if (index === 4) {
          navigate(`${rootPath}/metrics`);
        } else if (index === 5) {
          navigate(`${rootPath}/config`);
        } else {
          navigate(`${rootPath}/spans`);
        }
      });
    },
    [navigate, rootPath]
  );

  return (
    <main css={mainCSS}>
      <ProjectPageHeader
        project={data.project}
        extra={
          <Flex direction="row" alignItems="center" gap="size-100">
            <StreamToggle project={data.project} />
            <ConnectedTimeRangeSelector />
          </Flex>
        }
      />
      <ProjectPageQueryReferenceContext.Provider
        value={{
          spansQueryReference: spansQueryReference ?? null,
          sessionsQueryReference: sessionsQueryReference ?? null,
          tracesQueryReference: tracesQueryReference ?? null,
          projectConfigQueryReference: projectConfigQueryReference ?? null,
        }}
      >
        <Tabs
          onSelectionChange={(key) => {
            if (typeof key === "string" && isTab(key)) {
              onTabChange(TAB_INDEX_MAP[key]);
            }
          }}
          selectedKey={tab}
        >
          <TabList>
            <Tab id="spans">Spans</Tab>
            <Tab id="traces">Traces</Tab>
            <Tab id="sessions">Sessions</Tab>
            <Tab id="dashboard">Dashboard</Tab>
            <Tab id="metrics">Metrics</Tab>
            <Tab id="config">Config</Tab>
          </TabList>
          <LazyTabPanel padded={false} id="spans">
            <Outlet />
          </LazyTabPanel>
          <LazyTabPanel padded={false} id="traces">
            <Outlet />
          </LazyTabPanel>
          <LazyTabPanel padded={false} id="sessions">
            <Outlet />
          </LazyTabPanel>
          <LazyTabPanel padded={false} id="dashboard">
            <Outlet />
          </LazyTabPanel>
          <LazyTabPanel padded={false} id="metrics">
            <Outlet />
          </LazyTabPanel>
          <LazyTabPanel padded={false} id="config">
            <Outlet />
          </LazyTabPanel>
        </Tabs>
      </ProjectPageQueryReferenceContext.Provider>
    </main>
  );
}
